import React, { useState, useEffect, useRef, useCallback } from 'react';
import { findDOMNode } from 'react-dom';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import PhoneInput from 'react-phone-input-2';
import parsePhoneNumber from 'libphonenumber-js/mobile';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import registerActions from '../../../redux/actions/register.actions';
import HelpInfo from '../../HelpInfo/HelpInfo';
import alertsActions from '../../../redux/actions/alerts.actions';
import { REQUEST_STATUS } from '../../../models/common.model';
import { getUrlParam } from '../../../helpers/history';
import links from '../../../helpers/mainLinks';

export const getBrowserLanguage = () => navigator.language || navigator.userLanguage;
const PhoneVerificationForm = (props) => {
  const { activationCode } = props;
  const { search } = useLocation();
  const { i18n, t } = useTranslation();
  const isDesktopVersion = useMediaQuery({ minWidth: 768 });
  const dispatch = useDispatch();
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState();
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [closeVisible, setCloseVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const prevPhoneStatus = useRef(REQUEST_STATUS.IDLE);
  const phoneInputFocusHandlers = useRef(null);
  const { setPhoneNumberStatus, setPhoneNumberError } = useSelector((state) => state.register);
  let submitButtonRef = useRef(null);
  const { language } = i18n;
  const browserLang = (getBrowserLanguage() || '').split('-')[0];
  const isJoinWorkspace = Boolean(getUrlParam('joinWorkspace', search));
  const isJoinDocument = Boolean(getUrlParam('joinDocument', search));
  const phoneLang = language !== browserLang ? browserLang : language;
  const phoneInputLang = phoneLang === 'en' ? 'gb' : phoneLang;
  const mainLinks = links(language);

  useEffect(() => {
    if (
      prevPhoneStatus.current === REQUEST_STATUS.PENDING &&
      setPhoneNumberStatus === REQUEST_STATUS.ERROR
    ) {
      if (setPhoneNumberError?.errors?.length > 0) {
        switch (setPhoneNumberError.errors[0].code) {
          case 'PHONE_NUMBER_REGEX_NO_MATCH':
            setAlertVisible(true);
            break;
          case 'INVALID_PHONE_NUMBER':
            setAlertVisible(true);
            break;
          default:
            dispatch(alertsActions.error(t('phoneActivationScreen.incorrectNumber')));
            break;
        }
      } else {
        dispatch(alertsActions.error(t('common.errors.unknownError')));
      }
    }
    prevPhoneStatus.current = setPhoneNumberStatus;
  }, [setPhoneNumberStatus, setPhoneNumberError, dispatch, t]);

  const handleCountryDropdownOpen = useCallback(() => {
    const dropdown = document.getElementsByClassName('flag-dropdown')[0];
    setTimeout(() => {
      // eslint-disable-next-line react/no-find-dom-node
      const dropdownInputCollection = findDOMNode(dropdown).getElementsByClassName('search-box');
      const dropdownInput = [...dropdownInputCollection];
      if (dropdownInput) {
        dropdownInput.forEach((val) => {
          val.setAttribute('spellCheck', false);
          val.onkeydown = (event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
              return;
            }
            setTimeout(() => {
              const highlightedCountry = dropdown.querySelector(
                '.country-list li.highlight .country-name'
              );
              if (highlightedCountry) {
                const countryName = highlightedCountry.textContent;
                val.value = countryName;
                val.setAttribute('aria-label', countryName);
              }
            }, 0);
          };
          val.oninput = () => val.removeAttribute('aria-label');
        });
      }
    }, 1000);
  }, []);

  const setPhoneInputRef = useCallback((phoneInput) => {
    const previousHandlers = phoneInputFocusHandlers.current;
    if (previousHandlers) {
      previousHandlers.countrySelector.removeEventListener(
        'keydown',
        previousHandlers.handleCountrySelectorKeyDown
      );
      previousHandlers.phoneNumberInput.removeEventListener(
        'focus',
        previousHandlers.handlePhoneNumberInputFocus
      );
      previousHandlers.phoneNumberInput.removeEventListener(
        'blur',
        previousHandlers.handlePhoneNumberInputBlur
      );
      previousHandlers.phoneNumberInput.removeEventListener(
        'keydown',
        previousHandlers.handlePhoneNumberInputKeyDown
      );
      phoneInputFocusHandlers.current = null;
    }

    if (!phoneInput) {
      return;
    }

    // eslint-disable-next-line react/no-find-dom-node
    const phoneInputElement = findDOMNode(phoneInput);
    const countrySelector = phoneInputElement.querySelector('.selected-flag');
    const phoneNumberInput = phoneInputElement.querySelector('.form-control');
    if (!countrySelector || !phoneNumberInput) {
      return;
    }

    const handleCountrySelectorKeyDown = (event) => {
      if (event.key === 'Tab' && !event.shiftKey) {
        event.preventDefault();
        countrySelector.setAttribute('tabindex', '-1');
        phoneNumberInput.setAttribute('tabindex', '0');
        phoneNumberInput.focus();
      }
    };
    const handlePhoneNumberInputFocus = () => {
      countrySelector.setAttribute('tabindex', '-1');
      phoneNumberInput.setAttribute('tabindex', '0');
    };
    const handlePhoneNumberInputBlur = () => {
      countrySelector.setAttribute('tabindex', '0');
      phoneNumberInput.setAttribute('tabindex', '-1');
    };
    const handlePhoneNumberInputKeyDown = (event) => {
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        countrySelector.setAttribute('tabindex', '0');
        phoneNumberInput.setAttribute('tabindex', '-1');
        countrySelector.focus();
      }
    };

    countrySelector.setAttribute('tabindex', '0');
    phoneNumberInput.setAttribute('tabindex', '-1');
    countrySelector.addEventListener('keydown', handleCountrySelectorKeyDown);
    phoneNumberInput.addEventListener('focus', handlePhoneNumberInputFocus);
    phoneNumberInput.addEventListener('blur', handlePhoneNumberInputBlur);
    phoneNumberInput.addEventListener('keydown', handlePhoneNumberInputKeyDown);
    phoneInputFocusHandlers.current = {
      countrySelector,
      phoneNumberInput,
      handleCountrySelectorKeyDown,
      handlePhoneNumberInputFocus,
      handlePhoneNumberInputBlur,
      handlePhoneNumberInputKeyDown,
    };
  }, []);

  useEffect(() => {
    window.addEventListener('click', handleCountryDropdownOpen);
    return () => {
      window.removeEventListener('click', handleCountryDropdownOpen);
    };
  }, [handleCountryDropdownOpen]);

  useEffect(() => {
    const dropdown = document.getElementsByClassName('flag-dropdown')[0];
    dropdown.addEventListener(
      'click',
      () => {
        if (!closeVisible || dropdown.classList.contains('open')) {
          setCloseVisible(true);
        }
      },
      { once: true }
    );
  }, [closeVisible]);

  const splitPrefixFromPhone = () => {
    return {
      phoneNumber: phone.substr(phoneCountry.dialCode.length + 1),
      prefix: phoneCountry.dialCode,
    };
  };

  const validatePhoneNumber = (value, country) => {
    const input = document.getElementsByClassName('form-control')[0];
    let isValid = true;
    if (phone.length > 0) {
      const phoneNumber = parsePhoneNumber(value, country.iso2.toUpperCase());
      if (phoneNumber) {
        isValid = phoneNumber.isValid();
        if (isValid) {
          input.classList.add('valid-number');
        }
      }
    }
    return isValid;
  };

  const handleSubmitClick = () => {
    submitButtonRef.blur();
    setFormSubmitted(true);
    if (!isPhoneValid) {
      setAlertVisible(true);
    } else {
      const phoneData = splitPrefixFromPhone();
      dispatch(
        registerActions.addPhoneNumber(activationCode, phoneData.prefix, phoneData.phoneNumber)
      );
    }
  };

  const handleInputChange = (value, country) => {
    setCloseVisible(false);
    setPhone(value);
    setPhoneCountry(country);
    setAlertVisible(false);
    const dropdown = document.getElementsByClassName('flag-dropdown')[0];
    const searchInput = dropdown?.getElementsByClassName('search-box')[0];
    if (dropdown?.classList.contains('open') && searchInput && country?.name) {
      searchInput.value = country.name;
      searchInput.setAttribute('aria-label', country.name);
    }
    const phoneNumber = parsePhoneNumber(value, country.countryCode.toUpperCase());
    if (phoneNumber && phoneNumber.isValid()) {
      setIsPhoneValid(true);
    } else {
      setIsPhoneValid(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      handleSubmitClick();
    }
  };

  return (
    <Row>
      <Col xs={12}>
        <h1 className={clsx(!isDesktopVersion && 'h3 text-center', 'mb-2')}>
          {t('phoneActivationScreen.header')}
        </h1>
        {closeVisible && (
          <div className="test-close">
            <Button onKeyDown={handleKeyDown} variant="text" onClick={() => setCloseVisible(false)}>
              <i className="fico-doxy-close" />
            </Button>
          </div>
        )}
        {formSubmitted && alertVisible && (
          <div className="mb-6">
            <Alert
              className="d-flex w-100 justify-content-between align-items-center"
              variant="danger"
            >
              {t('phoneActivationScreen.incorrectNumber')}
              <Button
                className="m-0 p-0"
                variant="text"
                onClick={() => {
                  setAlertVisible(false);
                }}
              >
                <i className="fico-doxy-close" />
              </Button>
            </Alert>
          </div>
        )}
        <p className="text text-body-small text-color-light">
          {t('phoneActivationScreen.securityInfo')}
        </p>
        <p className="text text-body-small text-color-light">
          <span className="font-weight-bold">{t('phoneActivationScreen.securityInfoWarning')} </span>
          {t('phoneActivationScreen.securityInfoDetails')}
        </p>
        <PhoneInput
          searchPlaceholder={t('phoneActivationScreen.searchPlaceholder')}
          searchNotFound={t('phoneActivationScreen.emptyResults')}
          placeholder={t('phoneActivationScreen.inputPlaceholder')}
          country={phoneInputLang}
          value={phone}
          inputProps={{ tabIndex: -1 }}
          ref={setPhoneInputRef}
          onFocus={() => setCloseVisible(false)}
          onChange={(val, country) => handleInputChange(`+${val}`, country)}
          enableSearch
          disableSearchIcon
          copyNumbersOnly={false}
          isValid={(val, country) => validatePhoneNumber(`+${val}`, country)}
          onKeyDown={handleKeyDown}
        />
      </Col>
      <Col xs={12}>
        <Button
          type="button"
          variant="primary"
          className="w-100 mt-7"
          onClick={handleSubmitClick}
          ref={(buttonRefDOM) => {
            submitButtonRef = buttonRefDOM;
          }}
        >
          {t('phoneActivationScreen.button')}
        </Button>
      </Col>
      {(isJoinWorkspace || isJoinDocument) && (
        <Col xs={12} className="mt-4">
          <p className="text text-body-small text-color-light">
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: t('register.termsText', {
                  privacyPolicyUrl: mainLinks.privacyPolicy,
                  termsOfServiceUrl: mainLinks.termsOfService,
                  interpolation: { escapeValue: false },
                }),
              }}
            />
          </p>
        </Col>
      )}
      <Col xs={12} className={`${isJoinWorkspace || isJoinDocument ? 'mt-2' : 'mt-7'}`}>
        <div className="d-flex align-items-center justify-content-center flex-column flex-md-row ">
          <HelpInfo label={t('phoneActivationScreen.infoText')} linkLabel={t('support.link')} />
        </div>
      </Col>
    </Row>
  );
};

PhoneVerificationForm.propTypes = {
  activationCode: PropTypes.string,
};

export default PhoneVerificationForm;
